<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import debounce from "lodash/debounce";
import { ProductSorting, ProductStatus } from "mws-ts-v2-sdk";

import { queries } from "~/queries";
import { useEventQuery } from "~/queries/catalog/events";
import { useInfiniteProductsList } from "~/queries/catalog/products";

const {
  categoryIdentifier,
  eventIdentifier,
  isFilteringEnabled = true,
  initialFilters,
} = defineProps<{
  eventIdentifier?: string;
  categoryIdentifier?: string;
  isFilteringEnabled?: boolean;
  initialFilters?: {
    categoryIds?: string;
  };
}>();

const isEventQueryEnabled = computed(() => Boolean(eventIdentifier));
const { data: event } = useEventQuery(() => eventIdentifier, {
  enabled: isEventQueryEnabled,
});

const isCategoryQueryEnabled = computed(() => Boolean(categoryIdentifier));
const { data: category } = useQuery({
  ...queries.categories.details(() => categoryIdentifier),
  enabled: isCategoryQueryEnabled,
});

const { debounced: queryCategory } = useDebouncedRouteQuery("category", {
  defaultValue: () => initialFilters?.categoryIds,
});
const { debounced: queryPriceFrom } = useDebouncedRouteQuery("priceFrom");
const { debounced: queryPriceTo } = useDebouncedRouteQuery("priceTo");
const { debounced: queryStatus } =
  useDebouncedRouteQuery<ProductStatus>("productStatus");
const { debounced: querySorting } = useDebouncedRouteQuery<ProductSorting>(
  "sort",
  {
    defaultValue: ProductSorting.Featured,
  },
);

const productFilters = computed(() => ({
  eventIdentifier: eventIdentifier,
  categorySlug: category.value?.slug ?? null,
  categoryIds: transformRouteParams(queryCategory.value, "array"),
  priceFrom: transformRouteParams(queryPriceFrom.value, "string"),
  priceTo: transformRouteParams(queryPriceTo.value, "string"),
  status: transformRouteParams<ProductStatus>(queryStatus.value, "string"),
}));

const now = new Date();
const eventHasStarted = computed(() => {
  // we don't want to show the products before the event starts
  // this is a temporary solution; the backend will handle this in the future
  return !(!event.value || event.value.startDate > now);
});

const showProducts = computed(() => {
  return !eventIdentifier || eventHasStarted.value;
});

const enableFiltering = computed(() => {
  if (!isFilteringEnabled) return false;

  // We don't want to show the filter options for events that haven't started yet
  if (eventIdentifier) {
    return eventHasStarted.value;
  }

  return true;
});

const {
  data: products,
  hasNextPage,
  fetchNextPage,
  refetch,
  isLoading,
  isFetching,
  error,
} = useInfiniteProductsList(
  {
    filters: productFilters,
    sorting: querySorting,
  },
  {
    enabled: showProducts,
  },
);

// By default event updates are disabled, unless we're actually looking at the products for an event,
// and we know there are less than 100.
// We don't support live bid updates for large events right now because of performance concerns.
const eventUpdatesDisabled = computed(() => {
  const totalProducts = products.value?.totalCount;
  const isEvent = Boolean(event.value);

  return totalProducts === undefined || (isEvent && totalProducts >= 100);
});

useEventUpdates(event, {
  disabled: eventUpdatesDisabled,
});

// By default, set loading to true so we show the skeleton loader from the server
const productsAreLoading = ref(true);
watchEffect(() => {
  if (import.meta.client) {
    productsAreLoading.value = isLoading.value;
  }
});

const toast = useToast();
watch(error, (newError) => {
  if (!newError) return;
  toast.error(getErrorMessage(newError)[0]);
});

// debounce in case multiple products reach their endDate at the same time
const debounceRefetchProductsForEvent = debounce(refetch, 1000);

// scroll to the top of the products if the filters change
watch(
  [queryCategory, queryPriceFrom, queryPriceTo, queryStatus, querySorting],
  () => {
    scrollToStaticHeader();
  },
);
</script>

<template>
  <section class="page-container">
    <LazyProductsOverviewHeader
      v-if="enableFiltering"
      :product-count="products?.totalCount"
      :are-products-loading="productsAreLoading"
    >
      <ProductFilter
        v-if="event?.id"
        class="ml-auto"
        type="event"
        :event-identifier="event?.id"
      />
      <ProductFilter
        v-else-if="category?.id"
        class="ml-auto"
        type="category"
        :category-identifier="category?.id"
      />
    </LazyProductsOverviewHeader>

    <div class="block-container">
      <LazyBasePlaceholderNoResults
        v-if="!productsAreLoading && products && products.data.length === 0"
        variant="eventPage"
        class="m-16 site-viewport-container"
      />
      <ProductSectionProductsGrid
        :products="products?.data ?? []"
        :virtualisation="
          products?.totalCount !== undefined && products.totalCount > 25
        "
        :show-skeleton="productsAreLoading"
        :event="event"
        :has-margin="true"
        tracking-source="productsOverviewBlock"
        class="site-viewport-container"
        @product-finished="debounceRefetchProductsForEvent"
      />
      <LazyBasePaginatorShowMore
        v-if="hasNextPage && products?.totalCount"
        :amount-shown="products.data.length"
        class="site-viewport-container"
        :total-count="products.totalCount"
        :enable-autoscroll="false"
        :is-fetching="isFetching"
        @load-more="fetchNextPage"
      />
    </div>
  </section>
</template>

<style lang="postcss" scoped>
.header-transition {
  transition: position 0.3s ease;
}
</style>
